"use client";

import { useState, useEffect, useRef } from "react";
import "@mdxeditor/editor/style.css";

import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
  ListsToggle,
  CreateLink,
  InsertImage,
  InsertTable,
  InsertThematicBreak,
  BlockTypeSelect,
  Separator,
  rootEditor$,
  useCellValue,
  BoldItalicUnderlineToggles,
  StrikeThroughSupSubToggles,
  type MDXEditorMethods,
} from "@mdxeditor/editor";

import {
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  $createTextNode as createTextNode,
} from "lexical";
import { uploadFile } from "@/api/admin/uploadFile";
import { toast } from "@/hooks/useToast";
import {
  CircleIcon,
  Cross2Icon,
  TextIcon,
  FontSizeIcon,
  SquareIcon,
  GridIcon,
  Pencil1Icon,
  EyeOpenIcon,
  CheckIcon,
  TrashIcon,
  FontBoldIcon,
  FontItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  InfoCircledIcon,
  ImageIcon,
} from "@radix-ui/react-icons";

// Add interface for component props
interface MDXEditorInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

function ColorPickerButton() {
  const rootEditor = useCellValue(rootEditor$);
  const [color, setColor] = useState("#ff0000");
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Predefined colors from global CSS
  const PREDEFINED_COLORS = [
    // Primary colors
    { value: "#0a3d5b", name: "Primary Main" },
    { value: "#edf1f4", name: "Primary 1" },
    { value: "#e4eef6", name: "Primary 2" },
    { value: "#bcd0e0", name: "Primary 3" },
    { value: "#89a9c2", name: "Primary 4" },
    { value: "#5781a1", name: "Primary 5" },
    
    // Secondary colors
    { value: "#f74958", name: "Secondary Main" },
    { value: "#fff3f4", name: "Secondary 1" },
    { value: "#ffe9eb", name: "Secondary 2" },
    { value: "#ffdde0", name: "Secondary 3" },
    { value: "#ffd0d4", name: "Secondary 4" },
    { value: "#f8b6bc", name: "Secondary 5" },
    
    // Grayscale colors
    { value: "#101112", name: "Black" },
    { value: "#f5f5f5", name: "Gray 1" },
    { value: "#ededed", name: "Gray 2" },
    { value: "#c5c5c5", name: "Gray 3" },
    { value: "#848484", name: "Gray 4" },
    { value: "#5c5c5c", name: "Gray 5" },
    { value: "#373737", name: "Gray 6" },
    
    // Additional colors
    { value: "#f8f9fa", name: "White" },
    { value: "#4caf51", name: "Green" },
    { value: "#2196f3", name: "Blue" },
    { value: "#ff9800", name: "Orange" },
    { value: "#9c27b0", name: "Purple" },
  ];

  const updateButtonRect = () => {
    if (buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
  };

  const applyColorToSelection = (selectedColor: string) => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectedText = selection.getTextContent();
        if (!selectedText.trim()) return;

        const coloredNode = $createTextNode(selectedText);
        coloredNode.setStyle(`color: ${selectedColor};`);
        selection.insertNodes([coloredNode]);
      }
    });
    setIsOpen(false);
  };

  const applyBaseTextToSelection = () => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectedText = selection.getTextContent();
        if (!selectedText.trim()) return;

        const baseNode = $createTextNode(selectedText);
        baseNode.setStyle(`font-size: 14px; line-height: 1.5;`);
        selection.insertNodes([baseNode]);
      }
    });
    setIsOpen(false);
  };

  const applySquareTextToSelection = () => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectedText = selection.getTextContent();
        if (!selectedText.trim()) return;

        const squareNode = $createTextNode(selectedText);
        squareNode.setStyle(`font-size: 16px; line-height: 1.2; font-weight: 600; letter-spacing: 0.5px;`);
        selection.insertNodes([squareNode]);
      }
    });
    setIsOpen(false);
  };

  const removeColorFromSelection = () => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectedText = selection.getTextContent();
        const plainNode = $createTextNode(selectedText);
        selection.insertNodes([plainNode]);
      }
    });
    setIsOpen(false);
  };

  const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColor(e.target.value);
  };

  const handleColorInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applyColorToSelection(color);
    }
  };

  const handleButtonClick = () => {
    updateButtonRect();
    setIsOpen(!isOpen);
  };

  const ColorDropdown = () => {
    if (!isOpen || !buttonRect) return null;

    return (
      <div 
        className="fixed p-4 bg-white border border-gray-3 rounded-lg shadow-xl z-[99999] min-w-[320px]"
        style={{
          top: buttonRect.bottom + 4,
          left: buttonRect.left,
        }}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CircleIcon className="w-4 h-4 text-gray-7" />
              <h3 className="text-body-3-semibold text-gray-7">Text Formatting</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-5 hover:text-gray-7 transition-colors"
            >
              <Cross2Icon className="w-4 h-4" />
            </button>
          </div>
          
          {/* Text Style Options */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TextIcon className="w-4 h-4 text-gray-6" />
              <label className="text-body-4-medium text-gray-6">Text Styles:</label>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={applyBaseTextToSelection}
                className="flex-1 px-3 py-2 text-body-4-medium bg-gray-1 text-gray-7 border border-gray-3 rounded hover:bg-gray-2 transition-colors flex items-center justify-center gap-2"
                title="Apply base text styling (14px, normal weight)"
              >
                <FontSizeIcon className="w-4 h-4" />
                Base Text
              </button>
              <button
                type="button"
                onClick={applySquareTextToSelection}
                className="flex-1 px-3 py-2 text-body-4-medium bg-gray-1 text-gray-7 border border-gray-3 rounded hover:bg-gray-2 transition-colors flex items-center justify-center gap-2"
                title="Apply square text styling (16px, bold, spaced)"
              >
                <SquareIcon className="w-4 h-4" />
                Square Text
              </button>
            </div>
          </div>
          
          {/* Predefined Colors */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <GridIcon className="w-4 h-4 text-gray-6" />
              <label className="text-body-4-medium text-gray-6">Choose from design system colors:</label>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {PREDEFINED_COLORS.map((colorOption) => (
                <button
                  key={colorOption.value}
                  type="button"
                  onClick={() => {
                    setColor(colorOption.value);
                    applyColorToSelection(colorOption.value);
                  }}
                  className="w-8 h-8 rounded border border-gray-3 hover:scale-110 transition-transform cursor-pointer group relative"
                  style={{ backgroundColor: colorOption.value }}
                  title={`${colorOption.name} (${colorOption.value})`}
                >
                  {/* Color name tooltip */}
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-6 text-white text-body-4-medium px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    {colorOption.name}
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Custom Color Input */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Pencil1Icon className="w-4 h-4 text-gray-6" />
              <label className="text-body-4-medium text-gray-6">Or enter custom color:</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={color}
                onChange={handleColorInputChange}
                onKeyPress={handleColorInputKeyPress}
                placeholder="#000000 or red or rgb(255,0,0)"
                className="flex-1 px-3 py-2 border border-gray-3 rounded text-body-4-medium focus:outline-none focus:ring-2 focus:ring-primary-main focus:border-transparent"
              />
              <input
                type="color"
                value={color.startsWith('#') ? color : '#ff0000'}
                onChange={(e) => setColor(e.target.value)}
                className="w-10 h-10 border border-gray-3 rounded cursor-pointer"
                title="Color Picker"
              />
            </div>
          </div>
          
          {/* Color Preview */}
          <div className="flex items-center gap-3 p-3 bg-gray-1 rounded-lg">
            <EyeOpenIcon className="w-4 h-4 text-gray-6" />
            <span className="text-body-4-medium text-gray-6">Preview:</span>
            <div 
              className="w-8 h-8 border border-gray-3 rounded flex items-center justify-center"
              style={{ backgroundColor: color }}
              title={color}
            >
              <span 
                className="text-body-4-medium font-semibold"
                style={{ color: color === '#ffffff' || color === '#f8f9fa' ? '#000000' : '#ffffff' }}
              >
                A
              </span>
            </div>
            <span className="text-body-4-medium text-gray-5 font-mono">{color}</span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => applyColorToSelection(color)}
              className="flex-1 px-4 py-2 text-body-4-medium bg-primary-main text-white rounded hover:bg-primary-main/90 transition-colors flex items-center justify-center gap-2"
            >
              <CheckIcon className="w-4 h-4" />
              Apply Color
            </button>
            <button
              type="button"
              onClick={removeColorFromSelection}
              className="px-4 py-2 text-body-4-medium text-secondary-main border border-secondary-main rounded hover:bg-secondary-main hover:text-white transition-colors flex items-center gap-2"
            >
              <Cross2Icon className="w-4 h-4" />
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleButtonClick}
        className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors"
        title="Text Color & Formatting"
      >
        <CircleIcon className="w-4 h-4" />
      </button>
      <ColorDropdown />
    </>
  );
}

function CustomStyleButtons() {
  const rootEditor = useCellValue(rootEditor$);
  const [enabled, setEnabled] = useState(false);

  const checkIfSelectionHasStyle = () => {
    const editor = rootEditor;
    if (!editor) return;

    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        const hasStyle = nodes.some((node) => {
          const style = (node as any).getStyle?.();
          return !!style;
        });
        setEnabled(hasStyle);
      } else {
        setEnabled(false);
      }
    });
  };

  const mergeStyles = (existing: string, added: string) => {
    const styleObj: Record<string, string> = {};

    if (existing) {
      existing.split(";").forEach((s) => {
        const [key, value] = s.split(":").map((str) => str.trim());
        if (key && value) styleObj[key] = value;
      });
    }

    if (added) {
      added.split(";").forEach((s) => {
        const [key, value] = s.split(":").map((str) => str.trim());
        if (key && value) styleObj[key] = value;
      });
    }

    return Object.entries(styleObj)
      .map(([k, v]) => `${k}: ${v}`)
      .join("; ");
  };

  const applyStyle = (newStyle: string) => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectedText = selection.getTextContent();
        const nodes = selection.getNodes();

        let existingStyle = "";
        for (const node of nodes) {
          if (typeof (node as any).getStyle === "function") {
            existingStyle = (node as any).getStyle() || "";
            break;
          }
        }

        const mergedStyle = mergeStyles(existingStyle, newStyle);

        const styledNode = createTextNode(selectedText);
        styledNode.setStyle(mergedStyle);
        selection.insertNodes([styledNode]);
      }
    });
  };

  const applySuperscript = () => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectedText = selection.getTextContent();
        const nodes = selection.getNodes();

        let existingStyle = "";
        for (const node of nodes) {
          if (typeof (node as any).getStyle === "function") {
            existingStyle = (node as any).getStyle() || "";
            break;
          }
        }

        // Check if already superscript
        if (existingStyle.includes("vertical-align: super")) {
          // Remove superscript
          const newStyle = existingStyle
            .replace(/vertical-align:\s*super;?/g, "")
            .replace(/font-size:\s*0\.75em;?/g, "")
            .replace(/line-height:\s*0;?/g, "")
            .replace(/position:\s*relative;?/g, "")
            .replace(/top:\s*-0\.5em;?/g, "")
            .replace(/;+/g, ";")
            .replace(/^;|;$/g, "");
          
          const styledNode = createTextNode(selectedText);
          styledNode.setStyle(newStyle || "");
          selection.insertNodes([styledNode]);
        } else {
          // Apply superscript
          const superscriptStyle = "vertical-align: super; font-size: 0.75em; line-height: 0; position: relative; top: -0.5em;";
          const mergedStyle = mergeStyles(existingStyle, superscriptStyle);
          
          const styledNode = createTextNode(selectedText);
          styledNode.setStyle(mergedStyle);
          selection.insertNodes([styledNode]);
        }
      }
    });
  };

  const applySubscript = () => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const selectedText = selection.getTextContent();
        const nodes = selection.getNodes();

        let existingStyle = "";
        for (const node of nodes) {
          if (typeof (node as any).getStyle === "function") {
            existingStyle = (node as any).getStyle() || "";
            break;
          }
        }

        // Check if already subscript
        if (existingStyle.includes("vertical-align: sub")) {
          // Remove subscript
          const newStyle = existingStyle
            .replace(/vertical-align:\s*sub;?/g, "")
            .replace(/font-size:\s*0\.75em;?/g, "")
            .replace(/line-height:\s*0;?/g, "")
            .replace(/position:\s*relative;?/g, "")
            .replace(/bottom:\s*-0\.25em;?/g, "")
            .replace(/;+/g, ";")
            .replace(/^;|;$/g, "");
          
          const styledNode = createTextNode(selectedText);
          styledNode.setStyle(newStyle || "");
          selection.insertNodes([styledNode]);
        } else {
          // Apply subscript
          const subscriptStyle = "vertical-align: sub; font-size: 0.75em; line-height: 0; position: relative; bottom: -0.25em;";
          const mergedStyle = mergeStyles(existingStyle, subscriptStyle);
          
          const styledNode = createTextNode(selectedText);
          styledNode.setStyle(mergedStyle);
          selection.insertNodes([styledNode]);
        }
      }
    });
  };

  const removeSpanFromSelection = () => {
    const editor = rootEditor;
    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const plainText = selection.getTextContent();
        const plainNode = createTextNode(plainText);
        // Just a plain node, no styles
        selection.insertNodes([plainNode]);
      }
    });
  };

  return (
    <div
      className="flex items-center gap-1"
      onMouseEnter={checkIfSelectionHasStyle}
      onFocus={checkIfSelectionHasStyle}
    >
      <button
        type="button"
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => enabled && removeSpanFromSelection()}
        disabled={!enabled}
        title="Remove Styles"
      >
        <TrashIcon className="w-4 h-4 text-red-500" />
      </button>
      <button
        type="button"
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => enabled && applyStyle("font-weight: bold;")}
        disabled={!enabled}
        title="Bold"
      >
        <FontBoldIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => enabled && applyStyle("font-style: italic;")}
        disabled={!enabled}
        title="Italic"
      >
        <FontItalicIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => enabled && applyStyle("text-decoration: underline;")}
        disabled={!enabled}
        title="Underline"
      >
        <UnderlineIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => enabled && applyStyle("text-decoration: line-through;")}
        disabled={!enabled}
        title="Strikethrough"
      >
        <StrikethroughIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => enabled && applySuperscript()}
        disabled={!enabled}
        title="Superscript"
      >
        <ChevronUpIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-gray-2 transition-colors ${!enabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => enabled && applySubscript()}
        disabled={!enabled}
        title="Subscript"
      >
        <ChevronDownIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function MDXEditorInput({ 
  value = "", 
  onChange, 
  placeholder = "Start writing your content...",
  disabled = false 
}: MDXEditorInputProps) {
  const [insideStyledSpan, setInsideStyledSpan] = useState(false);
  const [isImageSelected, setIsImageSelected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const textEditorRef = useRef<MDXEditorMethods | null>(null);

  const checkIfSelectionInsideStyledSpan = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setInsideStyledSpan(false);
      return;
    }

    const range = selection.getRangeAt(0);
    let node: Node | null = range.startContainer;

    while (node) {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as Element).nodeName === "SPAN" &&
        (node as Element).hasAttribute("style")
      ) {
        setInsideStyledSpan(true);
        return;
      }
      node = node.parentNode;
    }

    setInsideStyledSpan(false);
  };

  function isEmpty(value: unknown): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim().length === 0)
    );
  }

  // Handle initial load to preserve HTML spans
  useEffect(() => {
    if (textEditorRef?.current && !isEmpty(value) && !isInitialized) {
      // On initial load, send the value through onChange to preserve HTML spans
      if (onChange && value) {
        setTimeout(() => {
          onChange(value);
        }, 100);
      }
      setIsInitialized(true);
    }
  }, [value, onChange, isInitialized]);

  useEffect(() => {
    if (textEditorRef?.current && !isEmpty(value)) {
      textEditorRef.current.setMarkdown(value);
    }

    const contentEditable = document.querySelector('[contenteditable="true"]');
    if (!contentEditable) return;

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;

      if (target.tagName === "IMG") {
        setIsImageSelected(true);
        const range = document.createRange();
        range.selectNode(target);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else {
        setIsImageSelected(false);
      }

      checkIfSelectionInsideStyledSpan();
    };

    const handleSelectionChange = () => {
      checkIfSelectionInsideStyledSpan();
    };

    contentEditable.addEventListener("click", handleClick);
    document.addEventListener("selectionchange", handleSelectionChange);
    
    return () => {
      contentEditable.removeEventListener("click", handleClick);
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [value]);

  return (
    <div className="w-full">
      <div className="border border-gray-3 rounded-lg overflow-hidden bg-white">
        <MDXEditor
          ref={textEditorRef}
          markdown={value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={disabled}
          plugins={[
            markdownShortcutPlugin(),
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            imagePlugin({
              imageUploadHandler: async (file: File) => {
                try {
                  const fileUrl = await uploadFile(file, toast);
                  if (!fileUrl) {
                    throw new Error("Upload failed - no URL returned");
                  }
                  return fileUrl;
                } catch (err) {
                  console.error("Image upload failed", err);
                  throw err;
                }
              },
            }),
            tablePlugin(),
            thematicBreakPlugin(),
            toolbarPlugin({
              toolbarContents: () => {
                return (
                  <div className="w-full flex flex-wrap items-center justify-start gap-1 p-2 bg-gray-1 border-b border-gray-3 overflow-hidden">
                    {/* Undo/Redo */}
                    <div className="flex items-center gap-1">
                      <UndoRedo />
                    </div>
                    
                    <Separator />
                    
                    {/* Block Type Selector */}
                    <div className="flex items-center gap-1">
                      <BlockTypeSelect />
                    </div>
                    
                    <Separator />
                    
                    {/* Text Formatting - Show custom or default based on span detection */}
                    <div className="flex items-center gap-1">
                      {insideStyledSpan ? (
                        // Custom span-based formatting tools
                        <CustomStyleButtons />
                      ) : (
                        // Default markdown formatting tools
                        <>
                          <BoldItalicUnderlineToggles />
                          <StrikeThroughSupSubToggles />
                        </>
                      )}
                    </div>
                    
                    <Separator />
                    
                    {/* Lists */}
                    <div className="flex items-center gap-1">
                      <ListsToggle />
                    </div>
                    
                    <Separator />
                    
                    {/* Links and Media */}
                    <div className="flex items-center gap-1">
                      {!isImageSelected && <CreateLink />}
                      <InsertImage />
                    </div>
                    
                    <Separator />
                    
                    {/* Tables and Thematic Breaks */}
                    <div className="flex items-center gap-1">
                      <InsertTable />
                      <InsertThematicBreak />
                    </div>
                    
                    <Separator />
                    
                    {/* Color Picker */}
                    <div className="flex items-center gap-1">
                      <ColorPickerButton />
                    </div>
                  </div>
                );
              },
            }),
          ]}
          contentEditableClassName="prose prose-sm max-w-none p-4 min-h-[200px] max-h-[400px] overflow-y-auto focus:outline-none font-sans text-sm leading-relaxed text-gray-8"
        />
      </div>
      
      {/* Help text */}
      <div className="mt-2 text-body-4-medium text-gray-5">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <InfoCircledIcon className="w-3 h-3" />
            Use markdown shortcuts or toolbar buttons
          </span>
          <span className="flex items-center gap-1">
            <CircleIcon className="w-3 h-3" />
            Choose colors and apply custom formatting
          </span>
          <span className="flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            Drag & drop images or use the image button
          </span>
        </div>
      </div>
    </div>
  );
}
